import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';
import '../styles/pages/_quiz.scss';

const quizQuestions = [
  {
    question: "Which approach is most effective for building financial resilience?",
    options: [
      "Gradual, consistent habits and planning",
      "Ignoring financial problems",
      "Borrowing money when needed"
    ],
    correctAnswer: 0,
    explanation: "Financial resilience is best built through gradual, consistent habits and planning. This includes regular saving, ongoing learning, and thoughtful financial decision-making rather than quick fixes or risky strategies."
  },
  {
    question: "What is the primary benefit of an emergency fund?",
    options: [
      "It provides a safety net that reduces financial stress",
      "It earns high interest rates",
      "It replaces the need for insurance"
    ],
    correctAnswer: 0,
    explanation: "An emergency fund provides a crucial safety net that reduces financial stress and prevents the need to go into debt during unexpected expenses or income disruptions."
  },
  {
    question: "How does diversification help build financial resilience?",
    options: [
      "It spreads risk across different investments and income sources",
      "It guarantees high returns",
      "It eliminates all financial risk"
    ],
    correctAnswer: 0,
    explanation: "Diversification spreads risk across different investments and income sources, reducing the impact of any single financial setback and building overall resilience."
  },
  {
    question: "Which habit is most important for long-term financial stability?",
    options: [
      "Regularly reviewing and adjusting your financial plan",
      "Following the latest investment trends",
      "Timing the market perfectly"
    ],
    correctAnswer: 0,
    explanation: "Regularly reviewing and adjusting your financial plan ensures it remains aligned with your changing needs, goals, and life circumstances, which is crucial for long-term stability."
  },
  {
    question: "What role does financial education play in resilience?",
    options: [
      "It helps you make informed decisions and adapt to changes",
      "It guarantees financial success",
      "It replaces professional financial advice"
    ],
    correctAnswer: 0,
    explanation: "Ongoing financial education helps you make informed decisions, understand financial products, and adapt to changing economic conditions, all of which contribute to financial resilience."
  }
];

const FinancialModule4QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('financialM4');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  useEffect(() => {
    if (responses[currentQuestion] !== undefined) {
      setSelectedAnswer(responses[currentQuestion]);
      setShowExplanation(true);
    } else {
      setSelectedAnswer(null);
      setShowExplanation(false);
    }
  }, [currentQuestion, responses]);

  const handleAnswerSelect = (answerIndex: number) => {
    setSelectedAnswer(answerIndex);
    setShowExplanation(true);
    setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const correctAnswers = Object.values(responses).filter((answer, index) =>
    answer === quizQuestions[index].correctAnswer
  ).length;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          Congratulations!
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 4 quiz!
        </p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              Key Achievements
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                Financial resilience strategies
              </li>
              <li>
                Long-term financial planning
              </li>
              <li>
                Risk management approaches
              </li>
            </ul>
          </div>
          <div className="next-steps-section">
            <h3 className="section-title">
              Next Steps
            </h3>
            <p>Continue your financial wellness journey by learning about maintaining financial wellness!</p>
            <p>Ready to explore habits for long-term financial success?</p>
          </div>
          <button
            className="resource-button"
            onClick={() => navigate('/panel/learn/financial-resources')}
          >
            Return to Financial Resources
          </button>
        </div>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left financial-panel">
          <div className="module-header">
            <h1>Module 4 Quiz</h1>
            <p className="module-subtitle">Building Financial Resilience</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned about building financial resilience and long-term stability.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>
            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>
              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? () => navigate('/panel/learn/financial-resources') : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={responses[currentQuestion] === undefined}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={responses[currentQuestion] === undefined}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialModule4QuizPage;

